// Diagnostic events only. generate_lead remains the final website conversion.
export const MEASUREMENT_VERSION = 'wa_visibility_v1'
export const CTA_VISIBLE_MS = 1000
export const CTA_VISIBLE_RATIO = 0.5

type Context = { lead_source: string; cta_placement: string; lp_category?: string; lead_item_name?: string }
type Emit = (event: string, params: Record<string, unknown>) => void
type View = { path: string; started: number; observed: Set<string>; visible: Set<string>; clicked: Set<string> }
let currentView: View | undefined

export function ctaContextKey(context: Context): string {
  return JSON.stringify([context.lead_source, context.cta_placement, context.lp_category || '', context.lead_item_name || ''])
}

export function pageType(path: string): string {
  if (path === '/') return 'home'
  if (path.startsWith('/produk/')) return 'product'
  if (path.startsWith('/kategori/')) return 'category'
  if (path.startsWith('/merek/')) return 'brand'
  if (path.startsWith('/blog/')) return 'blog'
  if (path.startsWith('/berita/')) return 'news'
  if (path === '/katalog') return 'catalog'
  if (path === '/search') return 'search'
  return 'landing_or_info'
}

export function getCtaClickContext(context: Context): Record<string, unknown> {
  if (typeof window === 'undefined' || !currentView || currentView.path !== window.location.pathname) return {}
  const key = ctaContextKey(context)
  const first = !currentView.clicked.has(key)
  currentView.clicked.add(key)
  const scrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
  return {
    measurement_version: MEASUREMENT_VERSION,
    page_type: pageType(currentView.path),
    cta_first_click: first ? 'yes' : 'no',
    cta_viewed: currentView.visible.has(key) ? 'yes' : currentView.observed.has(key) ? 'no' : 'untracked',
    seconds_to_lead: Math.max(0, Math.round((performance.now() - currentView.started) / 1000)),
    lead_scroll_percent: scrollable ? Math.max(0, Math.min(100, Math.round(window.scrollY / scrollable * 100))) : 0,
  }
}

/** One view per page visit and CTA context, after 50% visibility for 1 second. */
export function observeWhatsAppCtas(emit: Emit): () => void {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return () => {}
  const view: View = { path: window.location.pathname, started: performance.now(), observed: new Set(), visible: new Set(), clicked: new Set() }
  currentView = view
  const contexts = new Map<Element, Context>()
  const inViewport = new Set<Element>()
  const timers = new Map<Element, ReturnType<typeof setTimeout>>()
  const cancel = (element: Element) => {
    clearTimeout(timers.get(element))
    timers.delete(element)
  }
  const schedule = (element: Element) => {
    const context = contexts.get(element)
    if (!context || document.visibilityState !== 'visible' || timers.has(element)) return
    const key = ctaContextKey(context)
    if (view.visible.has(key)) return
    timers.set(element, setTimeout(() => {
      timers.delete(element)
      if (!element.isConnected || !inViewport.has(element) || document.visibilityState !== 'visible' || view.visible.has(key)) return
      // IO alone includes elements hidden behind overlays. Require a visible center.
      const rect = element.getBoundingClientRect()
      if (document.elementFromPoint) {
        const top = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2)
        if (!top || !element.contains(top)) return
      }
      const style = window.getComputedStyle(element)
      if (style.visibility === 'hidden' || style.display === 'none' || style.opacity === '0') return
      view.visible.add(key)
      emit('cta_view', {
        method: 'whatsapp', ...context,
        lp_category: context.lp_category || '', lead_item_name: context.lead_item_name || '',
        page_location: window.location.origin + view.path,
        page_path: view.path, page_type: pageType(view.path),
        measurement_version: MEASUREMENT_VERSION,
        visibility_threshold: CTA_VISIBLE_RATIO, visibility_duration_ms: CTA_VISIBLE_MS,
      })
    }, CTA_VISIBLE_MS))
  }
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio >= CTA_VISIBLE_RATIO) {
        inViewport.add(entry.target)
        schedule(entry.target)
      } else {
        inViewport.delete(entry.target)
        cancel(entry.target)
      }
    }
  }, { threshold: [0, CTA_VISIBLE_RATIO] })
  const scan = () => {
    for (const [element, oldContext] of contexts) {
      const raw = element.getAttribute('data-wa-cta')
      if (!element.isConnected || raw !== ctaContextKey(oldContext)) {
        cancel(element); inViewport.delete(element); contexts.delete(element); observer.unobserve(element)
      }
    }
    document.querySelectorAll('[data-wa-cta]').forEach((element) => {
      if (contexts.has(element)) return
      try {
        const fields: unknown = JSON.parse(element.getAttribute('data-wa-cta') || '')
        if (!Array.isArray(fields) || fields.length !== 4 || !fields.every((value) => typeof value === 'string') || !fields[0] || !fields[1]) return
        const context = { lead_source: fields[0], cta_placement: fields[1], lp_category: fields[2], lead_item_name: fields[3] }
        contexts.set(element, context)
        view.observed.add(ctaContextKey(context))
        observer.observe(element)
      } catch { /* Ignore malformed CMS annotations. */ }
    })
    // Retry an obscured CTA when a consent banner/modal is removed.
    for (const element of inViewport) schedule(element)
  }
  let scanPending = false
  const mutations = new MutationObserver(() => {
    if (scanPending) return
    scanPending = true
    queueMicrotask(() => { scanPending = false; if (currentView === view) scan() })
  })
  const visibilityChanged = () => {
    for (const element of timers.keys()) cancel(element)
    if (document.visibilityState === 'visible') for (const element of inViewport) schedule(element)
  }
  mutations.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-wa-cta', 'hidden', 'class', 'style'] })
  document.addEventListener('visibilitychange', visibilityChanged)
  scan()
  return () => {
    observer.disconnect(); mutations.disconnect()
    document.removeEventListener('visibilitychange', visibilityChanged)
    for (const element of timers.keys()) cancel(element)
    if (currentView === view) currentView = undefined
  }
}
